/**
 * Shared tag suggestions for Admin Products and Page Editor.
 * Matches Cranford catalog conventions (cape-breton, paul-cranford, lighthouse, …).
 */

const STOP_WORDS = new Set([
  'the', 'and', 'for', 'with', 'from', 'this', 'that', 'have', 'has', 'are', 'was', 'were',
  'been', 'will', 'can', 'could', 'would', 'should', 'about', 'into', 'through', 'during',
  'before', 'after', 'above', 'below', 'between', 'under', 'again', 'further', 'then', 'once',
  'here', 'there', 'when', 'where', 'why', 'how', 'all', 'both', 'each', 'few', 'more', 'most',
  'other', 'some', 'such', 'only', 'own', 'same', 'than', 'too', 'very', 'just', 'but', 'not',
  'out', 'off', 'over', 'also', 'your', 'you', 'our', 'their', 'its', 'new', 'edition', 'vol',
  'volume', 'book', 'cd', 'mp3', 'digital', 'download', 'package', 'series', 'collection',
]);

/** Longest phrases first — avoids matching "breton" before "cape breton". */
const PHRASE_RULES = [
  { pattern: /cape\s*[- ]?\s*breton/i, tags: ['cape-breton', 'cape breton'] },
  { pattern: /nova\s+scotia/i, tags: ['nova scotia'] },
  { pattern: /paul\s+s\.?\s*cranford|paul\s+cranford/i, tags: ['paul-cranford', 'paul cranford'] },
  { pattern: /cranford\s+publications/i, tags: ['cranford-publications'] },
  { pattern: /heritage\s+series|musical\s+heritage/i, tags: ['heritage', 'The Heritage Series'] },
  { pattern: /lighthouse\s+collection|the\s+lighthouse/i, tags: ['lighthouse'] },
  { pattern: /more\s+tunes/i, tags: ['more tunes', 'lighthouse'] },
  { pattern: /fiddler'?s?\s+choice/i, tags: ['fiddler', 'choice', 'jerry-holland'] },
  { pattern: /celtic\s+colours|celtic\s+colors/i, tags: ['celtic colours', 'collection'] },
  { pattern: /winston\s+fitzgerald/i, tags: ['winston-fitzgerald', 'winston fitzgerald'] },
  { pattern: /jerry\s+holland/i, tags: ['jerry-holland', 'jerry holland'] },
  { pattern: /brenda\s+stubbert/i, tags: ['brenda-stubbert', 'brenda stubbert'] },
  { pattern: /mario\s+colosimo/i, tags: ['mario colosimo', 'mario'] },
  { pattern: /johnny\s+wilmot/i, tags: ['johnny wilmot'] },
  { pattern: /doug\s+macphee|doug\s+macphee/i, tags: ['doug macphee'] },
  { pattern: /slow\s+air/i, tags: ['slow air', 'air'] },
  { pattern: /slip\s+jig/i, tags: ['slip jig', 'jig'] },
  { pattern: /pipe\s+music|bagpipe/i, tags: ['pipe', 'bagpipe'] },
  { pattern: /tin\s+whistle/i, tags: ['whistle', 'tin whistle'] },
  { pattern: /sheet\s+music|abc\s+notation/i, tags: ['sheet-music', 'notation'] },
  { pattern: /digital\s+download|mp3/i, tags: ['digital', 'download', 'mp3'] },
];

const KEYWORD_RULES = [
  { keywords: ['strathspey', 'strathspeys'], tag: 'strathspey' },
  { keywords: ['hornpipe', 'hornpipes'], tag: 'hornpipe' },
  { keywords: ['reel', 'reels'], tag: 'reel' },
  { keywords: ['jig', 'jigs'], tag: 'jig' },
  { keywords: ['waltz', 'waltzes'], tag: 'waltz' },
  { keywords: ['march', 'marches'], tag: 'march' },
  { keywords: ['polka', 'polkas'], tag: 'polka' },
  { keywords: ['fiddler', 'fiddling', 'fiddle'], tag: 'fiddle' },
  { keywords: ['accordion'], tag: 'accordion' },
  { keywords: ['concertina'], tag: 'concertina' },
  { keywords: ['piano'], tag: 'piano' },
  { keywords: ['guitar'], tag: 'guitar' },
  { keywords: ['violin'], tag: 'violin' },
  { keywords: ['celtic'], tag: 'celtic' },
  { keywords: ['scottish', 'scotland', 'scots'], tag: 'scottish' },
  { keywords: ['irish', 'ireland'], tag: 'irish' },
  { keywords: ['canadian', 'canada'], tag: 'canadian' },
  { keywords: ['shetland'], tag: 'shetland' },
  { keywords: ['gaelic', 'gaelic'], tag: 'gaelic' },
  { keywords: ['highland'], tag: 'highland' },
  { keywords: ['traditional', 'trad '], tag: 'traditional' },
  { keywords: ['folk'], tag: 'folk' },
  { keywords: ['biography', 'memoir'], tag: 'biography' },
  { keywords: ['heritage'], tag: 'heritage' },
  { keywords: ['review', 'reviewed'], tag: 'review' },
  { keywords: ['interview'], tag: 'interview' },
  { keywords: ['instruction', 'instructional', 'tutorial', 'method book'], tag: 'instruction' },
  { keywords: ['beginner', 'introductory', 'introduction'], tag: 'beginner' },
  { keywords: ['advanced', 'masterclass'], tag: 'advanced' },
  { keywords: ['tune', 'tunes', 'melody', 'melodies'], tag: 'tune' },
  { keywords: ['song', 'songs'], tag: 'song' },
  { keywords: ['collection', 'anthology', 'compilation'], tag: 'collection' },
  { keywords: ['bundle'], tag: 'bundle' },
  { keywords: ['notation', 'transcription'], tag: 'notation' },
  { keywords: ['chords'], tag: 'chords' },
  { keywords: ['dance', 'dancing'], tag: 'dance' },
  { keywords: ['composer', 'composed by'], tag: 'composer' },
  { keywords: ['arrangement', 'arranged'], tag: 'arrangement' },
];

const PAGE_TYPE_TAGS = {
  'tune-notation': ['tune', 'notation', 'sheet-music'],
  'abc-notation': ['abc', 'notation', 'sheet-music'],
  article: ['article'],
  'book-index': ['book-index', 'index', 'tune', 'collection'],
  review: ['review', 'article'],
  biography: ['biography', 'article'],
};

const PRODUCT_FORMAT_TAGS = {
  book: ['book', 'books'],
  cd: ['cd', 'audio'],
  audio: ['audio', 'cd'],
  bundle: ['bundle'],
  'digital download': ['digital', 'download', 'mp3'],
  mp3: ['digital', 'mp3', 'download'],
  video: ['video'],
  dvd: ['video', 'dvd'],
};

function normalizeTagKey(tag) {
  return String(tag || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function parseTagList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((tag) => String(tag).trim()).filter(Boolean);
  }
  return String(value)
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

export function mergeTagLists(existingTags, suggestedTags, maxTags = 18) {
  const seen = new Set();
  const merged = [];

  const push = (tag) => {
    const trimmed = String(tag || '').trim();
    if (!trimmed || trimmed.length < 2) return;
    const key = normalizeTagKey(trimmed);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(trimmed);
  };

  existingTags.forEach(push);
  suggestedTags.forEach(push);

  return merged.slice(0, maxTags);
}

export function toTagSlug(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[/\\]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function stripHtml(html) {
  return String(html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function addPersonTags(tags, name) {
  const trimmed = String(name || '').trim();
  if (!trimmed || trimmed.length > 48) return;

  const lower = trimmed.toLowerCase();
  tags.add(lower);

  const slug = toTagSlug(trimmed);
  if (slug && slug.length >= 3 && slug.length <= 40) {
    tags.add(slug);
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  const nameParts = parts.filter((part) => !/^[a-z]\.?$/i.test(part));

  if (nameParts.length >= 2) {
    const last = nameParts[nameParts.length - 1].toLowerCase();
    if (last.length >= 3 && !STOP_WORDS.has(last)) {
      tags.add(last);
    }
    const lastTwo = `${nameParts[nameParts.length - 2]}-${nameParts[nameParts.length - 1]}`.toLowerCase();
    const lastTwoSlug = toTagSlug(lastTwo);
    if (lastTwoSlug && lastTwoSlug.length >= 5) tags.add(lastTwoSlug);
  }
}

function addFromCorpus(tags, corpus) {
  const text = corpus.toLowerCase();

  PHRASE_RULES.forEach(({ pattern, tags: phraseTags }) => {
    if (pattern.test(text)) {
      phraseTags.forEach((tag) => tags.add(tag));
    }
  });

  KEYWORD_RULES.forEach(({ keywords, tag }) => {
    if (keywords.some((keyword) => text.includes(keyword))) {
      tags.add(tag);
    }
  });
}

function addDistinctiveTitleTags(tags, title) {
  if (!title) return;

  const words = title
    .toLowerCase()
    .replace(/[^a-z0-9\s'-]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length >= 4 && !STOP_WORDS.has(word));

  words.slice(0, 2).forEach((word) => {
    if (!['from', 'with', 'tunes', 'music', 'fiddle', 'traditional'].includes(word)) {
      tags.add(word);
    }
  });
}

function orderTags(tagSet, priorityKeys = []) {
  const all = [...tagSet];
  const priority = [];
  const rest = [];

  all.forEach((tag) => {
    const key = normalizeTagKey(tag);
    const idx = priorityKeys.findIndex((p) => key.includes(p) || p.includes(key));
    if (idx >= 0) {
      priority.push({ tag, idx });
    } else {
      rest.push(tag);
    }
  });

  priority.sort((a, b) => a.idx - b.idx || a.tag.localeCompare(b.tag));
  rest.sort((a, b) => a.localeCompare(b));

  return [...priority.map((entry) => entry.tag), ...rest];
}

export function suggestProductTags(product = {}) {
  const tags = new Set();
  const corpus = [
    product.name,
    product.description,
    product.category,
    product.type,
    product.item_type,
    product.sku,
    product.seo_title,
    product.seo_description,
    product.index_page_id,
  ]
    .filter(Boolean)
    .join(' ');

  const plainCorpus = stripHtml(corpus);
  addFromCorpus(tags, plainCorpus);

  const category = String(product.category || product.type || '').toLowerCase();
  const itemType = String(product.item_type || '').toLowerCase();

  Object.entries(PRODUCT_FORMAT_TAGS).forEach(([key, formatTags]) => {
    if (category.includes(key) || itemType.includes(key)) {
      formatTags.forEach((tag) => tags.add(tag));
    }
  });

  if (product.is_bundle === 1 || product.is_bundle === true) {
    tags.add('bundle');
  }
  if (product.is_digital_album === 1 || product.is_digital_album === true) {
    ['digital', 'download', 'mp3'].forEach((tag) => tags.add(tag));
  }

  addPersonTags(tags, product.author);
  addPersonTags(tags, product.artist);
  addDistinctiveTitleTags(tags, product.name);

  if (product.sku) {
    const sku = String(product.sku).toUpperCase();
    if (sku.includes('LC') || sku.includes('LIGHT')) tags.add('lighthouse');
    if (sku.includes('HERIT') || sku.includes('CBH')) tags.add('heritage');
    if (sku.includes('MTL')) tags.add('more tunes');
  }

  if (product.index_page_id) {
    const slug = toTagSlug(product.index_page_id);
    if (slug) tags.add(slug);
  }

  tags.add('cranford-publications');

  return orderTags(tags, [
    'lighthouse', 'heritage', 'more tunes', 'cape-breton', 'bundle', 'digital',
    'book', 'cd', 'fiddle', 'traditional', 'collection', 'paul-cranford',
  ]).slice(0, 18);
}

export function suggestPageTags(page = {}, options = {}) {
  const { linkedProducts = [] } = options;
  const metadata = page.metadata || {};
  const tags = new Set();

  const corpus = [
    page.title,
    page.description,
    metadata.seo_description,
    metadata.composer,
    metadata.author,
    metadata.category,
    metadata.region,
    metadata.tune_type,
    metadata.collection_book,
    metadata.key_signature,
    metadata.time_signature,
    page.pageId,
    page.page_id,
    stripHtml(page.html),
  ]
    .filter(Boolean)
    .join(' ');

  addFromCorpus(tags, corpus);

  (PAGE_TYPE_TAGS[page.type] || []).forEach((tag) => tags.add(tag));

  if (metadata.region) {
    tags.add(toTagSlug(metadata.region) || metadata.region.toLowerCase());
    tags.add(metadata.region.toLowerCase());
  }
  if (metadata.tune_type) {
    tags.add(toTagSlug(metadata.tune_type));
  }
  if (metadata.composer) {
    addPersonTags(tags, metadata.composer);
  }
  if (metadata.author) {
    addPersonTags(tags, metadata.author);
  }
  if (metadata.collection_book) {
    const slug = toTagSlug(metadata.collection_book);
    if (slug && slug.length <= 48) tags.add(slug);
  }
  if (metadata.key_signature) {
    const keyTag = toTagSlug(metadata.key_signature);
    if (keyTag) tags.add(keyTag);
  }
  if (metadata.time_signature) {
    tags.add(`time-${String(metadata.time_signature).replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '')}`);
  }

  linkedProducts.forEach((product) => {
    suggestProductTags(product).slice(0, 8).forEach((tag) => tags.add(tag));
  });

  const pageId = String(page.pageId || page.page_id || '');
  if (pageId.includes('-index') || pageId.includes('index')) {
    tags.add('book-index');
    tags.add('index');
  }

  if (tags.size === 0) {
    tags.add(page.type ? page.type.replace(/-/g, ' ') : 'content');
  }

  return orderTags(tags, [
    'lighthouse', 'heritage', 'cape-breton', 'tune', 'notation', 'book-index',
    'article', 'review', 'fiddle', 'traditional', 'paul-cranford',
  ]).slice(0, 18);
}

export function formatTagsForInput(tags) {
  return mergeTagLists([], tags).join(', ');
}
